import { useCallback, useState } from "react";

import { useDesignOptionId } from "./overlay/useDesignOptionId";
import {
  useDesignWorkspaceOptions,
  type DesignWorkspaceDesign,
} from "./useDesignWorkspaceOptions";
import useDesignsRepository from "./useDesignsRepository";

const FROZEN_WORKFLOW_STAGES = new Set(["approved", "implemented", "closed"]);

type DesignOption = {
  id: string;
  name: string;
  isTrunk: boolean;
};

export type UseDesignOptionActionsOverride = {
  design: DesignWorkspaceDesign | null | undefined;
  refetch: () => Promise<unknown>;
};

/**
 * By default resolves the design (and its options) via the workspace
 * context. When `override` is supplied, the actions instead operate on an
 * already-fetched `design` and use the caller's own `refetch` — for
 * callers (e.g. the design detail page) that already loaded the design by
 * id and don't need a second workspace-scoped fetch.
 */
export const useDesignOptionActions = (
  override?: UseDesignOptionActionsOverride
) => {
  const { optionId, setOptionId } = useDesignOptionId();
  const {
    enabled,
    isDesignWorkspace,
    design,
    options,
    optionsLoading,
    refetch,
  } = useDesignWorkspaceOptions(override);
  const designsRepository = useDesignsRepository();
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const designId = design?.id;
  const baselineDataDate = design?.baselineDataDate ?? null;
  const frozen = FROZEN_WORKFLOW_STAGES.has(design?.workflowStage ?? "");

  const createOption = useCallback(
    async (name: string) => {
      if (!designId || frozen) return;
      setCreating(true);
      try {
        const created = await designsRepository.createDesignOption({
          id: designId,
          name,
        });
        await refetch();
        if (created?.id) {
          setOptionId(created.id);
        }
      } finally {
        setCreating(false);
      }
    },
    [designId, designsRepository, frozen, refetch, setOptionId]
  );

  const renameOption = useCallback(
    async (option: DesignOption, name: string) => {
      if (!designId || frozen || option.isTrunk) return;
      setRenaming(true);
      try {
        await designsRepository.renameDesignOption({
          id: designId,
          optionId: option.id,
          name,
        });
        await refetch();
      } finally {
        setRenaming(false);
      }
    },
    [designId, designsRepository, frozen, refetch]
  );

  const deleteOption = useCallback(
    async (option: DesignOption) => {
      if (!designId || frozen || option.isTrunk) return;
      const deleted = await designsRepository.deleteDesignOption({
        id: designId,
        optionId: option.id,
      });
      if (!deleted) return;
      await refetch();
    },
    [designId, designsRepository, frozen, refetch]
  );

  const flattenOption = useCallback(
    async (option: DesignOption) => {
      if (!designId || frozen || option.isTrunk) return;
      const flattened = await designsRepository.flattenDesignOption({
        id: designId,
        optionId: option.id,
      });
      if (!flattened) return;
      await refetch();
    },
    [designId, designsRepository, frozen, refetch]
  );

  return {
    // Show once the sandbox design resolves so "New option…" can seed trunk.
    visible: enabled && isDesignWorkspace && Boolean(design),
    optionId,
    setOptionId,
    options,
    optionsLoading,
    baselineDataDate,
    frozen,
    creating,
    renaming,
    createOption,
    renameOption,
    deleteOption,
    flattenOption,
  };
};

export default useDesignOptionActions;
